import { DeleteStages } from 'constants/deleteStages';
import { EMPTY_FIELD } from 'constants/emptyField';
import { OperationAccess } from 'constants/operationAccesses';
import { Resources } from 'constants/resources';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RuleIcon from '@mui/icons-material/Rule';
import { blue } from '@mui/material/colors';
import { useCreatePathWithQuery } from 'hooks/useCreatePathWithQuery';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { useStagedDelete } from 'hooks/useStagedDelete';
import { GATEWAY_DUPLICATE_SOURCE_PARAM } from 'pages/Gateways/GatewaysCreate/hooks/useGatewayCreateDefaultValues';
import { queryClient } from 'providers/queryClient';
import { RaRecord, useListController, useNotify } from 'react-admin';
import { Link as RouterLink } from 'react-router-dom';
import { Box } from 'shared/mui/Box';
import { CancelButton, CreateButton } from 'shared/mui/Buttons';
import { Chip } from 'shared/mui/Chips';
import {
  ContextMenu,
  ContextMenuButton,
  ContextMenuDeleteButton,
  ContextMenuEditButton,
  ContextMenuEntityUserActionsButton,
  ContextMenuShowButton,
} from 'shared/mui/ContextMenu';
import { DeleteStageDialog } from 'shared/mui/Dialogs/DeleteStageDialog/DeleteStageDialog';
import { DeleteStageDialogDenyReason } from 'shared/mui/Dialogs/DeleteStageDialog/DeleteStageDialogDenyReason';
import { Link } from 'shared/mui/Link/Link';
import { Stack } from 'shared/mui/Stack/Stack';
import { Typography } from 'shared/mui/Typography';
import { TypographyBold } from 'shared/mui/Typography/TypographyBold';
import { DatagridStickyContainer } from 'shared/react-admin/Datagrids/DatagridSticky/DatagridStickyContainer';
import {
  BooleanField,
  DateTimeField,
  FunctionField,
  GatewayStatusIconField,
  TextField,
  TooltipTextField,
} from 'shared/react-admin/Fields';
import { ListPage } from 'shared/react-admin/Pages';
import { ProviderShowDialog } from 'shared/widgets/ProviderShowDialog/ProviderShowDialog';
import { Gateway, GatewayMethod } from 'types/embedded/gateway';
import { Permission } from 'types/embedded/permission';
import { Provider } from 'types/embedded/provider';

import { DatagridStyled, ChipStyled } from './GatewaysList.styled';
import { GatewaysListFilter } from './GatewaysListFilter/GatewaysListFilter';

export const GatewaysList: React.FC = () => {
  const { filterValues } = useListController({
    resource: Resources.Gateways,
    queryOptions: { enabled: false },
  });
  const { createPathWithQuery } = useCreatePathWithQuery();
  const { hasResourceAccess, hasResourcePermissions, hasOperationAccess } = usePermissions();
  const { record, handleOpenRecordModal, openRecordModal, handleCloseRecordModal } =
    useRecordModal<Gateway>();
  const {
    record: provider,
    handleCloseRecordModal: handleCloseRecordModalProvider,
    openRecordModal: openRecordModalProvider,
    handleOpenRecordModal: handleOpenRecordModalProvider,
  } = useRecordModal<Provider>();

  const notify = useNotify();
  const { deleteOneRecord, denyDeleteReason, stage, setStage } = useStagedDelete({
    record,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [Resources.Gateways] });
      handleCloseRecordModal();
      setStage(DeleteStages.Initial);
      notify('Шлюз успешно удален!', { type: 'success' });
    },
  });

  const handleCloseDeleteDialog = () => {
    setStage(DeleteStages.Initial);
    handleCloseRecordModal();
  };

  return (
    <ListPage
      actions={[
        hasResourcePermissions(Resources.Gateways, [Permission.Full, Permission.Create]) && (
          <CreateButton key="create_gateways" />
        ),
      ]}
      empty={false}
      filter={filterValues}
      headerTitle={{
        titleText: 'Шлюзы',
        tooltipText:
          'Технический канал для проведения платежей с помощью одного или нескольких методов, предоставляемых провайдером. Шлюз - это папка с кодом, отвечающим за взаимодействие с API провайдера для проведения платежей с помощью его методов и связанная с ней через алиас шлюза запись в таблице. При интеграции с провайдером для каждого предоставляемого им метода нужно создавать шлюз. Например, существует провайдер PagSmile, которые предоставляет методы Boleto и PSE. При интеграции с PagSmile для каждого метода нужно создать в административной панели шлюзы PagSmile Boleto и PagSmile PSE и реализовать код для взаимодействия с API PagSmile для каждого метода.',
      }}
      sort={{ field: 'id', order: 'DESC' }}
    >
      <GatewaysListFilter />
      <DatagridStickyContainer>
        <DatagridStyled bulkActionButtons={false}>
          <TextField label="ID" sortBy="id" source="originId" />
          <FunctionField
            cellClassName="column-name"
            label="Название"
            render={(record: Gateway) => (
              <Link
                component={RouterLink}
                to={createPathWithQuery({
                  resource: Resources.Gateways,
                  id: record?.id,
                  type: 'show',
                })}
              >
                {record?.name}
              </Link>
            )}
            source="name"
          />
          <TooltipTextField label="Внешний алиас" sortable={false} source="externalAlias" />
          <TooltipTextField label="Алиас" source="alias" />
          <GatewayStatusIconField
            cellClassName="column-status"
            label="Статус"
            sx={{ display: 'flex', minWidth: '100px' }}
          />
          {hasOperationAccess(OperationAccess.ManageMerchantAccounts) && (
            <BooleanField label="Проверен менеджером" sortable={false} source="managerVerified" />
          )}
          {hasResourceAccess(Resources.Providers) && (
            <FunctionField
              cellClassName="column-provider"
              label="Провайдер"
              render={(record: { provider?: Provider }) =>
                record?.provider ? (
                  <TextField
                    onClick={() => handleOpenRecordModalProvider(record.provider!)}
                    record={record.provider}
                    source="name"
                    sx={{
                      cursor: 'pointer',
                      color: blue[700],
                    }}
                  />
                ) : (
                  '-'
                )
              }
            />
          )}
          <FunctionField
            cellClassName="column-direction"
            label="Направление"
            render={(record: RaRecord) =>
              record?.direction ? (
                record.direction.description === 'Все направления' ? (
                  <Stack direction="row" gap="4px">
                    <Chip label="Пополнение" />
                    <Chip label="Вывод" />
                  </Stack>
                ) : (
                  <ChipStyled label={record.direction.description} />
                )
              ) : (
                '—'
              )
            }
          />
          <BooleanField label="PCI DSS" source="pcidss" />
          <BooleanField label="Push API" source="pushApi" />
          {hasResourceAccess('gateway_methods') && (
            <FunctionField
              cellClassName="column-gatewayMethods"
              label="Методы"
              render={(record: Gateway) =>
                record?.gatewayMethods?.length ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {record.gatewayMethods.map((method: GatewayMethod) => (
                      <ChipStyled key={method.id} label={method.methodName} />
                    ))}
                  </Box>
                ) : (
                  EMPTY_FIELD
                )
              }
            />
          )}
          {hasResourceAccess(Resources.Currencies) && (
            <FunctionField
              label="Валюта по умолч."
              render={(record: Gateway) => record?.defaultCurrency?.alphaCode ?? EMPTY_FIELD}
            />
          )}
          {hasResourceAccess(Resources.Currencies) && (
            <FunctionField
              label="Поддерж. валюты"
              render={(record: Gateway) => {
                return (
                  <Stack direction="row" flexWrap="wrap" gap="4px">
                    {record?.supportedCurrencies?.map((c: any) => (
                      <Chip key={c.id} label={c.alphaCode} />
                    ))}
                  </Stack>
                );
              }}
            />
          )}
          <DateTimeField label="Дата создания" source="createdAt" />
          <DateTimeField label="Дата редактир." source="updatedAt" />
          <ContextMenu
            render={(record: Gateway) => [
              hasResourcePermissions(Resources.Gateways, [Permission.Full, Permission.Create]) && (
                <RouterLink
                  key="duplicate-button"
                  style={{ textDecoration: 'none' }}
                  target="_blank"
                  to={createPathWithQuery(
                    { resource: Resources.Gateways, type: 'create' },
                    { [GATEWAY_DUPLICATE_SOURCE_PARAM]: record.originId },
                  )}
                >
                  <ContextMenuButton
                    icon={<ContentCopyIcon fontSize="small" />}
                    label="Дублировать шлюз"
                  />
                </RouterLink>
              ),
              hasResourceAccess(Resources.GatewayRules) && (
                <RouterLink
                  key="gateway-rules-button"
                  style={{ textDecoration: 'none' }}
                  target="_blank"
                  to={createPathWithQuery(
                    { resource: Resources.GatewayRules, type: 'list' },
                    { filter: { 'mappings.mappingItems.gateway': [record.id] } },
                  )}
                >
                  <ContextMenuButton
                    icon={<RuleIcon fontSize="small" />}
                    label="Перейти к правилам"
                  />
                </RouterLink>
              ),
              hasResourceAccess(Resources.UserActions) && (
                <ContextMenuEntityUserActionsButton
                  entityName="gateway"
                  key="user-actions-button"
                />
              ),
              <ContextMenuShowButton key="show-button" />,
              hasResourcePermissions(Resources.Gateways, [Permission.Full, Permission.Update]) && (
                <ContextMenuEditButton key="edit-button" />
              ),
              hasResourcePermissions(Resources.Gateways, [Permission.Full, Permission.Delete]) && (
                <ContextMenuDeleteButton
                  key="delete-button"
                  onClick={() => handleOpenRecordModal(record)}
                />
              ),
            ]}
          />
        </DatagridStyled>
      </DatagridStickyContainer>
      <DeleteStageDialog
        deleteOneRecord={deleteOneRecord}
        details={
          <Typography>
            Шлюз <TypographyBold fontSize={16} text={record?.name || ''} /> будет удален без
            возможности восстановления.
          </Typography>
        }
        onClose={handleCloseDeleteDialog}
        open={openRecordModal}
        stage={stage}
        title="Удаление шлюза"
      >
        <DeleteStageDialogDenyReason reason={denyDeleteReason}>
          <CancelButton
            key="history-back-button"
            label="Вернуться к списку шлюзов"
            onClick={handleCloseDeleteDialog}
            variant="outlined"
          />
        </DeleteStageDialogDenyReason>
      </DeleteStageDialog>
      {provider && (
        <ProviderShowDialog
          onClose={handleCloseRecordModalProvider}
          open={openRecordModalProvider}
          provider={provider}
        />
      )}
    </ListPage>
  );
};
